import { ReactNode } from 'react';
import draftToHtml from 'draftjs-to-html';
import pluralize from 'pluralize';

import { EBOOK_INTERACTIVE, INTERACTIVE } from 'global/constants';
import { ScreenReaderOnly } from 'approot/shared/styles/global.styles';
import InlineSvg from 'approot/shared/inline-svg/inline-svg';
import ImageThumbnail from 'approot/shared/images/images';
import * as Styled from './stimulus-search-item.style';
import type { SearchStimulusResourcesQuery } from '../stimulus.graphql.generated';
import { getRegionalisedLink } from 'approot/shared/logic/get-regionalised-link';
import type { Pluck } from 'lib/gql/pluck';

type Props = {
  stimulus: Pluck<
    SearchStimulusResourcesQuery,
    'searchStimulusResources.stimulusResources'
  >;
  action?: ReactNode;
};

const StimulusSearchItem = ({ stimulus, action }: Props) => {
  const contentMarkup = draftToHtml(stimulus.description);

  let link = stimulus.slug;
  if (
    stimulus.type !== INTERACTIVE &&
    stimulus.type !== EBOOK_INTERACTIVE &&
    'link' in stimulus
  ) {
    link = stimulus.link;
  }

  const usage = [...stimulus.lessons, ...stimulus.assessments].length;

  return (
    <Styled.StimulusItem data-selector="stimulus-item">
      <Styled.StimulusItemTrigger
        href={getRegionalisedLink(link || '')}
        target="_blank"
      >
        <ScreenReaderOnly>
          {stimulus.name}, {stimulus.type}, Used in {usage}{' '}
          {pluralize('lesson', usage)}
        </ScreenReaderOnly>
      </Styled.StimulusItemTrigger>
      <Styled.StimulusItemWrapper>
        <Styled.StimulusItemThumbnail
          aria-hidden
          data-selector="stimulus-thumbnail"
        >
          <ImageThumbnail
            width={96}
            images={stimulus.coverImage?.responsive || []}
          />
          <Styled.StimulusItemIcon>
            <InlineSvg id={stimulus.type} />
          </Styled.StimulusItemIcon>
        </Styled.StimulusItemThumbnail>
        <Styled.StimulusItemDetails aria-hidden>
          <Styled.StimulusItemPreTitle data-selector="usage-count">
            Used in {usage} {pluralize('lesson', usage)}
          </Styled.StimulusItemPreTitle>
          <Styled.StimulusItemTitle data-selector="stimulus-name">
            {stimulus.name}
          </Styled.StimulusItemTitle>
          <Styled.StimulusItemText
            data-selector="stimulus-item-text"
            dangerouslySetInnerHTML={{ __html: contentMarkup }}
          ></Styled.StimulusItemText>
        </Styled.StimulusItemDetails>
        {action && (
          <Styled.StimulusItemActions data-selector="btn-action">
            {action}
          </Styled.StimulusItemActions>
        )}
      </Styled.StimulusItemWrapper>
    </Styled.StimulusItem>
  );
};

export default StimulusSearchItem;
